import { Graphics, Text } from 'pixi.js';
import { BaseScene } from './BaseScene.js';
import { MenuScene } from './MenuScene.js';
import { GameState } from '../core/GameState.js';
import { DebugLog } from '../core/DebugLog.js';
import { GAME_WIDTH, GAME_HEIGHT } from '../main.js';

/**
 * Game Over screen showing run statistics, crypt tokens earned, and lifetime stats.
 */
export class GameOverScene extends BaseScene {
  init() {
    DebugLog.info('GameOverScene', 'Showing game over screen');

    this._blinkTimer = 0;
    this._promptVisible = true;

    // Calculate tokens earned this run (matches recordRunEnd formula)
    this._tokensEarned = GameState.floor * 5 + Math.floor(GameState.enemiesDefeated / 10);

    // Dark background with red tint
    const bg = new Graphics();
    bg.rect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    bg.fill(0x100005);
    this.container.addChild(bg);

    // Red vignette overlay
    const overlay = new Graphics();
    overlay.rect(0, 0, GAME_WIDTH, GAME_HEIGHT);
    overlay.fill({ color: 0x330000, alpha: 0.4 });
    this.container.addChild(overlay);

    // "YOU HAVE FALLEN" title
    const title = new Text({
      text: 'YOU HAVE FALLEN',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 22,
        fontWeight: 'bold',
        fill: 0xcc2222,
        letterSpacing: 4,
      }
    });
    title.anchor.set(0.5);
    title.x = GAME_WIDTH / 2;
    title.y = 40;
    this.container.addChild(title);

    // Separator line
    const separator = new Graphics();
    separator.rect(GAME_WIDTH / 2 - 80, 58, 160, 1);
    separator.fill(0x662222);
    this.container.addChild(separator);

    // Build run stats
    const runDuration = GameState.getRunDuration();
    const minutes = Math.floor(runDuration / 60);
    const seconds = Math.floor(runDuration % 60);
    const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;

    const stats = [
      `Floor reached: ${GameState.floor}`,
      `Enemies defeated: ${GameState.enemiesDefeated}`,
      `Gold collected: ${GameState.goldCollected}`,
      `Time survived: ${timeStr}`,
    ];

    const statsStartY = 75;
    const statsSpacing = 16;

    for (let i = 0; i < stats.length; i++) {
      const statText = new Text({
        text: stats[i],
        style: {
          fontFamily: 'Courier New, monospace',
          fontSize: 11,
          fill: 0xaa9988,
        }
      });
      statText.anchor.set(0.5);
      statText.x = GAME_WIDTH / 2;
      statText.y = statsStartY + i * statsSpacing;
      this.container.addChild(statText);
    }

    // Crypt tokens earned section (M10)
    const tokenY = statsStartY + stats.length * statsSpacing + 12;

    const tokenSep = new Graphics();
    tokenSep.rect(GAME_WIDTH / 2 - 60, tokenY, 120, 1);
    tokenSep.fill(0x443355);
    this.container.addChild(tokenSep);

    const tokenText = new Text({
      text: `+${this._tokensEarned} Crypt Tokens`,
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 12,
        fontWeight: 'bold',
        fill: 0xbb88ff,
      }
    });
    tokenText.anchor.set(0.5);
    tokenText.x = GAME_WIDTH / 2;
    tokenText.y = tokenY + 14;
    this.container.addChild(tokenText);

    // Lifetime stats section (M10)
    const lifetimeY = tokenY + 36;

    const lifetimeHeader = new Text({
      text: '-- Lifetime --',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 9,
        fill: 0x667788,
      }
    });
    lifetimeHeader.anchor.set(0.5);
    lifetimeHeader.x = GAME_WIDTH / 2;
    lifetimeHeader.y = lifetimeY;
    this.container.addChild(lifetimeHeader);

    const lifetimeStats = [
      `Total runs: ${GameState.totalRuns}`,
      `Best floor: ${GameState.bestFloor}`,
      `Total kills: ${GameState.totalEnemiesDefeated}`,
      `Crypt Tokens: ${GameState.cryptTokens}`,
    ];

    for (let i = 0; i < lifetimeStats.length; i++) {
      const lsText = new Text({
        text: lifetimeStats[i],
        style: {
          fontFamily: 'Courier New, monospace',
          fontSize: 9,
          fill: 0x556677,
        }
      });
      lsText.anchor.set(0.5);
      lsText.x = GAME_WIDTH / 2;
      lsText.y = lifetimeY + 14 + i * 13;
      this.container.addChild(lsText);
    }

    // Blinking prompt
    this._prompt = new Text({
      text: 'Press ENTER to return to menu',
      style: {
        fontFamily: 'Courier New, monospace',
        fontSize: 10,
        fill: 0x886666,
      }
    });
    this._prompt.anchor.set(0.5);
    this._prompt.x = GAME_WIDTH / 2;
    this._prompt.y = GAME_HEIGHT - 30;
    this.container.addChild(this._prompt);

    DebugLog.info('GameOverScene', 'Game over screen ready');
  }

  update(dt) {
    const input = this.sceneManager.inputManager;

    // Blink the prompt
    this._blinkTimer += dt;
    if (this._blinkTimer >= 0.5) {
      this._blinkTimer -= 0.5;
      this._promptVisible = !this._promptVisible;
      this._prompt.visible = this._promptVisible;
    }

    // Return to menu on Enter
    if (input.isKeyJustPressed('Enter')) {
      DebugLog.info('GameOverScene', 'Returning to menu');
      this.sceneManager.switchTo(new MenuScene(this.sceneManager, this.eventBus));
    }
  }
}
